import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiClient } from '../services/authService';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    if (!user) {
      setPermissions(null);
      setLoading(false);
      return;
    }
    
    try {
      const response = await apiClient.get('/auth/permissions/');
      setPermissions(response.data.permissions);
      console.log('Permissions chargées:', response.data.permissions);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Charger les permissions au montage et quand l'utilisateur change
  useEffect(() => {
    // Ne pas conclure à une absence de permissions pendant que la session
    // utilisateur est encore en cours de restauration.
    if (authLoading) {
      setLoading(true);
      return;
    }

    setLoading(true);
    fetchPermissions();
  }, [authLoading, fetchPermissions]);

  /**
   * Rafraîchir les permissions manuellement
   */
  const refreshPermissions = useCallback(async () => {
    setLoading(true);
    await fetchPermissions();
  }, [fetchPermissions]);

  /**
   * Vérifie si l'utilisateur a la permission pour une action sur une ressource.
   * @param {string} resource - La ressource (ex: 'sessions', 'formations')
   * @param {string} action - L'action (ex: 'view', 'create', 'edit', 'delete')
   * @returns {boolean} - True si l'utilisateur a la permission
   */
  const can = useCallback((resource, action) => {
    if (!permissions) return false;
    return permissions[resource]?.[action] === true;
  }, [permissions]);

  /**
   * Vérifie si l'utilisateur peut voir une ressource.
   */
  const canView = useCallback((resource) => can(resource, 'view'), [can]);

  /**
   * Vérifie si l'utilisateur peut créer une ressource.
   */
  const canCreate = useCallback((resource) => can(resource, 'create'), [can]);

  /**
   * Vérifie si l'utilisateur peut modifier une ressource.
   */
  const canEdit = useCallback((resource) => can(resource, 'edit'), [can]);

  /**
   * Vérifie si l'utilisateur peut supprimer une ressource.
   */
  const canDelete = useCallback((resource) => can(resource, 'delete'), [can]);

  /**
   * Vérifie si l'utilisateur peut télécharger les documents d'une ressource.
   */
  const canDownload = useCallback((resource) => can(resource, 'download'), [can]);

  /**
   * Vérifie si l'utilisateur peut valider une ressource.
   */
  const canValidate = useCallback((resource) => can(resource, 'validate'), [can]);

  /**
   * Vérifie si l'utilisateur peut refuser une ressource.
   */
  const canReject = useCallback((resource) => can(resource, 'reject'), [can]);

  /**
   * Vérifie si la ressource doit être affichée dans le menu.
   */
  const canDisplay = useCallback((resource) => can(resource, 'display'), [can]);

  const value = {
    permissions,
    loading,
    can,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canDownload,
    canValidate,
    canReject,
    canDisplay,
    refreshPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export default PermissionsContext;
